import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, Lock, Mail, Copy, ExternalLink, CheckCircle2, AlertCircle, Send, Image as ImageIcon, Sparkles, Pencil } from 'lucide-react';
import { PromoImageDialog } from './PromoImageDialog';
import { EditRosterDialog } from './EditRosterDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

export function TeamManagementPanel({ eventId, teams, isPro, initialLockAfterGames = 0, eventName = '', shareToken = '' }) {
  const [rosterLock, setRosterLock] = useState(initialLockAfterGames || 0);
  const [contacts, setContacts] = useState({});
  const [editingTeam, setEditingTeam] = useState(null);
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [readiness, setReadiness] = useState(null);
  const [reminderSendingId, setReminderSendingId] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [editRosterTeam, setEditRosterTeam] = useState(null);

  useEffect(() => {
    setRosterLock(initialLockAfterGames || 0);
  }, [initialLockAfterGames]);

  useEffect(() => {
    const existing = {};
    (teams || []).forEach(t => {
      if (t.contact) existing[t.id] = t.contact;
    });
    setContacts(existing);
  }, [teams]);

  const fetchReadiness = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${eventId}/roster-readiness`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setReadiness(await res.json());
    } catch {}
  }, [eventId]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  const saveLockRule = async () => {
    const token = localStorage.getItem('session_token');
    const res = await fetch(`${API_URL}/api/events/${eventId}/roster-lock`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ lock_after_games: rosterLock })
    });
    if (res.ok) toast.success(rosterLock > 0 ? `Roster locks after ${rosterLock} game(s)` : 'Roster lock removed');
  };

  const setTeamContact = async (teamId) => {
    if (!contactEmail.trim()) { toast.error('Enter an email'); return; }
    const token = localStorage.getItem('session_token');
    const res = await fetch(`${API_URL}/api/events/${eventId}/teams/${teamId}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ email: contactEmail, name: contactName })
    });
    if (res.ok) {
      const data = await res.json();
      setContacts(prev => ({ ...prev, [teamId]: { email: contactEmail, name: contactName, team_token: data.team_token } }));
      setEditingTeam(null);
      setContactEmail('');
      setContactName('');
      toast.success(data.email_sent ? 'Contact set & invitation email sent!' : 'Contact set! Share the roster link with them.');
      fetchReadiness();
    }
  };

  const sendReminder = async (teamId) => {
    setReminderSendingId(teamId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${eventId}/teams/${teamId}/remind`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Reminder email sent to ${data.email}`);
        fetchReadiness();
      } else {
        toast.error(data.detail || 'Failed to send reminder');
      }
    } catch { toast.error('Network error'); }
    finally { setReminderSendingId(null); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${FRONTEND_URL}/team/${token}`);
    toast.success('Roster link copied!');
  };

  if (!isPro) return null;

  const readinessByTeam = {};
  (readiness?.teams || []).forEach(t => { readinessByTeam[t.team_id] = t; });
  const submittedCount = readiness?.submitted_count || 0;
  const totalContacts = (readiness?.teams || []).filter(t => t.has_contact).length;

  return (
    <Card className="mt-6" data-testid="team-management-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
          <h3 className="text-lg font-bold text-[#00247D] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Users className="w-5 h-5" /> TEAM MANAGEMENT
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {totalContacts > 0 && (
              <div className="flex items-center gap-2 text-xs" data-testid="readiness-summary">
                <div className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-emerald-700 font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {submittedCount} / {totalContacts} submitted
                </div>
              </div>
            )}
            <Button
              size="sm"
              onClick={() => setPromoOpen(true)}
              className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-black font-semibold shadow-sm h-8"
              data-testid="promote-on-social-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Promote on Social
            </Button>
          </div>
        </div>

        {/* Roster Lock Rule */}
        <div className="flex items-center gap-3 mb-6 p-3 bg-slate-50 rounded-lg">
          <Lock className="w-4 h-4 text-slate-500 shrink-0" />
          <span className="text-sm text-slate-600">Lock rosters after</span>
          <Input
            type="number" min="0" value={rosterLock}
            onChange={e => setRosterLock(parseInt(e.target.value) || 0)}
            className="w-16 text-center h-8" data-testid="roster-lock-input"
          />
          <span className="text-sm text-slate-600">game(s)</span>
          <Button size="sm" variant="outline" onClick={saveLockRule} data-testid="save-lock-btn">Save</Button>
          <span className="text-xs text-slate-400">{rosterLock === 0 ? 'No lock' : ''}</span>
        </div>

        {/* Team Contacts */}
        <div className="space-y-3">
          {(teams || []).map(team => {
            const contact = contacts[team.id];
            const status = readinessByTeam[team.id];
            const submitted = status?.submitted;
            return (
              <div key={team.id} className="flex flex-wrap items-center justify-between p-3 border border-slate-200 rounded-lg gap-y-2" data-testid={`team-contact-${team.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 text-sm">{team.name}</span>
                    {contact && (
                      submitted ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700" data-testid={`status-submitted-${team.id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Submitted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-700" data-testid={`status-pending-${team.id}`}>
                          <AlertCircle className="w-3 h-3" /> Pending
                        </span>
                      )
                    )}
                    {status?.player_count > 0 && (
                      <span className="text-[10px] text-slate-400">{status.player_count} player{status.player_count === 1 ? '' : 's'}</span>
                    )}
                    {status?.has_team_photo && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400" title="Team photo uploaded">
                        <ImageIcon className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </div>
                  {contact ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-500">{contact.email}</span>
                      {contact.name && <span className="text-xs text-slate-400">({contact.name})</span>}
                      {status?.last_reminder_sent_at && (
                        <span className="text-[10px] text-slate-400" title={status.last_reminder_sent_at}>
                          · last reminder {new Date(status.last_reminder_sent_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">No contact set</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {contact?.team_token && (
                    <>
                      <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => copyLink(contact.team_token)} data-testid={`copy-link-${team.id}`}>
                        <Copy className="w-3 h-3 mr-1" /> Copy Link
                      </Button>
                      <a href={`/team/${contact.team_token}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="text-xs h-7"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                      {!submitted && (
                        <Button variant="outline" size="sm" className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => sendReminder(team.id)} disabled={reminderSendingId === team.id}
                          data-testid={`remind-${team.id}`}>
                          <Send className="w-3 h-3 mr-1" /> {reminderSendingId === team.id ? 'Sending...' : 'Remind'}
                        </Button>
                      )}
                    </>
                  )}
                  <Button variant="outline" size="sm" className="text-xs h-7 border-[#00247D]/30 text-[#00247D] hover:bg-blue-50"
                    onClick={() => setEditRosterTeam(team)}
                    data-testid={`edit-roster-${team.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit Roster
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-7"
                    onClick={() => { setEditingTeam(editingTeam === team.id ? null : team.id); setContactEmail(contact?.email || ''); setContactName(contact?.name || ''); }}
                    data-testid={`edit-contact-${team.id}`}>
                    {contact ? 'Edit' : 'Set Contact'}
                  </Button>
                </div>
                {editingTeam === team.id && (
                  <div className="w-full mt-3 flex gap-2 pt-3 border-t border-slate-100">
                    <Input placeholder="Contact name" value={contactName} onChange={e => setContactName(e.target.value)} className="flex-1 h-8 text-sm" data-testid={`contact-name-${team.id}`} />
                    <Input placeholder="Contact email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="flex-1 h-8 text-sm" data-testid={`contact-email-${team.id}`} />
                    <Button size="sm" className="bg-[#00247D] hover:bg-[#001A5C] h-8" onClick={() => setTeamContact(team.id)} data-testid={`save-contact-${team.id}`}>Save</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
      <PromoImageDialog
        open={promoOpen}
        onOpenChange={setPromoOpen}
        eventId={eventId}
        eventName={eventName}
        shareToken={shareToken}
      />
      {editRosterTeam && (
        <EditRosterDialog
          open={!!editRosterTeam}
          onOpenChange={(open) => { if (!open) setEditRosterTeam(null); }}
          eventId={eventId}
          team={editRosterTeam}
          onSaved={() => { fetchReadiness(); }}
        />
      )}
    </Card>
  );
}
